// =============================================================================
// FILE: packages/renderer/src/components/dashboard/SystemOverviewDiagram.tsx
// PURPOSE: Tabbed SVG Command Station diagram with:
//   • Full-screen modal (Maximize2 button → fixed overlay portal)
//   • Zoom & Pan  (mouse-wheel + click-drag, centred on cursor)
//   • High-contrast, large-type labels for legibility at any zoom level
//   Tab A — Project Topology   : live hierarchy from useDashboardStats
//   Tab B — Engine Architecture: static BIM OPS platform diagram
// =============================================================================

import React, { useState, useRef, useCallback, useEffect, type JSX } from 'react';
import { createPortal } from 'react-dom';
import {
  Network, Cpu, Maximize2, Minimize2,
  ZoomIn, ZoomOut, RotateCcw, X,
} from 'lucide-react';
import { useDashboardStats } from '../../hooks/useDashboard.js';
import type { Route } from '../../App.js';

type Tab = 'topology' | 'architecture';
interface DiagramProps { onNavigate?: (r: Route) => void }

// ─── Content dimensions (natural coordinate space) ───────────────────────────
const TOPO_W = 980;  const TOPO_H = 300;
const ARCH_W = 960;  const ARCH_H = 420;

// ─── Node geometry ────────────────────────────────────────────────────────────
const NW = 154; const NH = 80;    // topology node card
const EW = 150; const EH = 66;    // architecture engine card

// =============================================================================
//  Public component
// =============================================================================

export function SystemOverviewDiagram({ onNavigate }: DiagramProps): JSX.Element {
  const [tab,        setTab]        = useState<Tab>('topology');
  const [fullscreen, setFullscreen] = useState(false);

  // Escape key closes fullscreen
  useEffect(() => {
    if (!fullscreen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setFullscreen(false); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [fullscreen]);

  const header = (
    <DiagramHeader
      tab={tab} setTab={setTab}
      fullscreen={fullscreen} setFullscreen={setFullscreen}
    />
  );

  const canvas = (
    <ZoomPanCanvas
      contentW={tab === 'topology' ? TOPO_W : ARCH_W}
      contentH={tab === 'topology' ? TOPO_H : ARCH_H}
      fullscreen={fullscreen}
      tab={tab}
    >
      {tab === 'topology'
        ? <TopologyContent onNavigate={fullscreen ? undefined : onNavigate} />
        : <ArchitectureContent />
      }
    </ZoomPanCanvas>
  );

  return (
    <>
      {/* ── Inline (dashboard) view ── */}
      <section data-testid="system-overview-diagram" className="mb-10">
        {header}
        <div
          className="rounded-sm overflow-hidden"
          style={{
            height: 300,
            background: 'rgba(10,14,22,0.95)',
            border: '1px solid rgba(37,181,168,0.18)',
            boxShadow: '0 0 40px rgba(37,181,168,0.04)',
          }}
        >
          {canvas}
        </div>
      </section>

      {/* ── Full-screen portal ── */}
      {fullscreen && createPortal(
        <div
          data-testid="diagram-fullscreen-overlay"
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgb(8 12 22)',
            display: 'flex', flexDirection: 'column',
          }}
        >
          <div className="shrink-0 flex items-center justify-between px-5 py-3"
            style={{ borderBottom: '1px solid rgba(37,181,168,0.2)' }}
          >
            {header}
            <button
              type="button"
              data-testid="diagram-fullscreen-close"
              onClick={() => setFullscreen(false)}
              className="ml-4 h-8 w-8 grid place-items-center rounded-sm text-zinc-500 hover:text-zinc-200 transition"
              style={{ border: '1px solid rgba(63,63,70,0.6)' }}
              title="Close full-screen (Esc)"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <ZoomPanCanvas
              contentW={tab === 'topology' ? TOPO_W : ARCH_W}
              contentH={tab === 'topology' ? TOPO_H : ARCH_H}
              fullscreen
              tab={tab}
            >
              {tab === 'topology'
                ? <TopologyContent onNavigate={onNavigate} onClose={() => setFullscreen(false)} />
                : <ArchitectureContent />
              }
            </ZoomPanCanvas>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

// =============================================================================
//  Header bar (shared between inline + full-screen)
// =============================================================================

function DiagramHeader({ tab, setTab, fullscreen, setFullscreen }: {
  tab: Tab; setTab: (t: Tab) => void;
  fullscreen: boolean; setFullscreen: (v: boolean) => void;
}): JSX.Element {
  return (
    <div className="flex items-center justify-between mb-3 flex-1">
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Command Station</div>
        <h3 className="text-sm font-semibold text-zinc-200 mt-0.5">System Overview</h3>
      </div>
      <div className="flex items-center gap-2">
        {/* Tab switcher */}
        <div className="flex items-center gap-1 p-0.5 rounded-sm"
          style={{ background: 'rgba(24,24,27,0.9)', border: '1px solid rgba(63,63,70,0.6)' }}>
          {([
            { id: 'topology' as Tab,      icon: Network, label: 'Project Topology' },
            { id: 'architecture' as Tab,  icon: Cpu,     label: 'Engine Architecture' },
          ] as const).map(({ id, icon: Icon, label }) => (
            <button key={id} type="button" data-testid={`diagram-tab-${id}`}
              onClick={() => setTab(id)}
              className={[
                'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-[10px] uppercase tracking-[0.16em] transition-all',
                tab === id
                  ? 'bg-teal-500/20 text-teal-200 border border-teal-500/40'
                  : 'text-zinc-500 hover:text-zinc-300 border border-transparent',
              ].join(' ')}
            >
              <Icon className="h-3 w-3" />
              {label}
            </button>
          ))}
        </div>
        {/* Full-screen toggle */}
        <button
          type="button"
          data-testid={fullscreen ? 'diagram-exit-fullscreen-btn' : 'diagram-fullscreen-btn'}
          onClick={() => setFullscreen(!fullscreen)}
          title={fullscreen ? 'Exit full-screen' : 'Full-screen (Esc to close)'}
          className="h-8 w-8 grid place-items-center rounded-sm text-zinc-500 hover:text-teal-300 transition-all"
          style={{ border: '1px solid rgba(63,63,70,0.6)', background: 'rgba(24,24,27,0.9)' }}
        >
          {fullscreen ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
        </button>
      </div>
    </div>
  );
}

// =============================================================================
//  Zoom / Pan canvas engine
// =============================================================================

interface ZPState { zoom: number; pan: { x: number; y: number } }

function ZoomPanCanvas({ contentW, contentH, children, fullscreen, tab }: {
  contentW: number; contentH: number;
  children: React.ReactNode;
  fullscreen: boolean; tab: Tab;
}): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(1);
  const [pan,  setPan]  = useState({ x: 0, y: 0 });
  const stateRef = useRef<ZPState>({ zoom: 1, pan: { x: 0, y: 0 } });
  const dragRef  = useRef<{ active: boolean; sx: number; sy: number; spx: number; spy: number }>({
    active: false, sx: 0, sy: 0, spx: 0, spy: 0,
  });

  const fitView = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const w = el.clientWidth; const h = el.clientHeight;
    const fitZoom = Math.min((w / contentW) * 0.94, (h / contentH) * 0.94);
    const newPan  = { x: (w - contentW * fitZoom) / 2, y: (h - contentH * fitZoom) / 2 };
    stateRef.current = { zoom: fitZoom, pan: newPan };
    setZoom(fitZoom); setPan(newPan);
  }, [contentW, contentH]);

  // Fit on mount, tab change, fullscreen toggle
  useEffect(() => { fitView(); }, [fitView, tab, fullscreen]);

  const applyZoom = useCallback((factor: number, cx: number, cy: number) => {
    const { zoom: z, pan: p } = stateRef.current;
    const newZoom = Math.max(0.12, Math.min(12, z * factor));
    const newPan  = { x: cx - (cx - p.x) * (newZoom / z), y: cy - (cy - p.y) * (newZoom / z) };
    stateRef.current = { zoom: newZoom, pan: newPan };
    setZoom(newZoom); setPan(newPan);
  }, []);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const rect = containerRef.current!.getBoundingClientRect();
    applyZoom(e.deltaY < 0 ? 1.14 : 1 / 1.14, e.clientX - rect.left, e.clientY - rect.top);
  }, [applyZoom]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    const { pan: p } = stateRef.current;
    dragRef.current = { active: true, sx: e.clientX, sy: e.clientY, spx: p.x, spy: p.y };
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!dragRef.current.active) return;
    const { sx, sy, spx, spy } = dragRef.current;
    const newPan = { x: spx + (e.clientX - sx), y: spy + (e.clientY - sy) };
    stateRef.current = { ...stateRef.current, pan: newPan };
    setPan(newPan);
  }, []);

  const stopDrag = useCallback(() => { dragRef.current.active = false; }, []);

  const zoomIn    = () => { const r = containerRef.current!.getBoundingClientRect(); applyZoom(1.25, r.width / 2, r.height / 2); };
  const zoomOut   = () => { const r = containerRef.current!.getBoundingClientRect(); applyZoom(1 / 1.25, r.width / 2, r.height / 2); };
  const pct       = Math.round(zoom * 100);
  const isDragging = dragRef.current.active;

  return (
    <div
      ref={containerRef}
      style={{ width: '100%', height: '100%', overflow: 'hidden', position: 'relative',
               cursor: isDragging ? 'grabbing' : 'grab' }}
    >
      <svg
        width="100%" height="100%"
        data-testid={`${tab}-svg`}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={stopDrag}
        onMouseLeave={stopDrag}
        style={{ display: 'block', userSelect: 'none' }}
      >
        <g transform={`translate(${pan.x},${pan.y}) scale(${zoom})`}>
          {children}
        </g>
      </svg>

      {/* ── Zoom controls (absolute-positioned over the SVG) ── */}
      <div style={{
        position: 'absolute', bottom: 10, right: 10,
        display: 'flex', alignItems: 'center', gap: 4,
      }}>
        {/* Zoom % badge */}
        <span style={{
          fontSize: 10, fontFamily: 'monospace',
          color: 'rgba(148,172,210,0.55)', letterSpacing: '0.06em',
          padding: '2px 6px', borderRadius: 4,
          background: 'rgba(10,14,22,0.85)',
          border: '1px solid rgba(40,68,116,0.5)',
        }}>
          {pct}%
        </span>
        <ZCBtn onClick={zoomOut}   title="Zoom out" icon={<ZoomOut  className="h-3 w-3" />} />
        <ZCBtn onClick={fitView}   title="Reset view" icon={<RotateCcw className="h-3 w-3" />} />
        <ZCBtn onClick={zoomIn}    title="Zoom in"  icon={<ZoomIn   className="h-3 w-3" />} />
      </div>

      {/* ── Interaction hint (fades after first use) ── */}
      <div style={{
        position: 'absolute', bottom: 10, left: 10,
        fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase',
        color: 'rgba(148,172,210,0.30)', pointerEvents: 'none', fontFamily: 'sans-serif',
      }}>
        Scroll · zoom   Drag · pan
      </div>
    </div>
  );
}

function ZCBtn({ onClick, title, icon }: { onClick: () => void; title: string; icon: React.ReactNode }): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      style={{
        width: 26, height: 26, display: 'grid', placeItems: 'center',
        borderRadius: 5, cursor: 'pointer',
        background: 'rgba(10,14,22,0.88)',
        border: '1px solid rgba(40,68,116,0.55)',
        color: 'rgba(148,172,210,0.75)',
      }}
      onMouseOver={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(37,181,168,0.55)'; (e.currentTarget as HTMLButtonElement).style.color = '#5EEAD4'; }}
      onMouseOut={(e) =>  { (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(40,68,116,0.55)'; (e.currentTarget as HTMLButtonElement).style.color = 'rgba(148,172,210,0.75)'; }}
    >
      {icon}
    </button>
  );
}

// =============================================================================
//  Topology content  (pure SVG <g> — no <svg> wrapper)
// =============================================================================

const TOPOLOGY_ROUTES: Record<string, Route> = {
  projects: 'projects', buildings: 'buildings', racks: 'racks',
  assets: 'assets', clashes: 'clashes',
};

function TopologyContent({ onNavigate, onClose }: {
  onNavigate?: (r: Route) => void;
  onClose?: () => void;
}): JSX.Element {
  const { data, isLoading } = useDashboardStats();
  const [hovered, setHovered] = useState<string | null>(null);

  const nodes = [
    { id: 'projects',      label: 'Projects',        count: data?.projects      ?? 0, color: '#2DD4BF', x: 18,  y: 100 },
    { id: 'buildings',     label: 'Buildings',        count: data?.buildings     ?? 0, color: '#38BDF8', x: 218, y: 100 },
    { id: 'telecomSpaces', label: 'Telecom Spaces',   count: data?.telecomSpaces ?? 0, color: '#818CF8', x: 418, y: 44  },
    { id: 'racks',         label: 'Racks',             count: data?.racks         ?? 0, color: '#F59E0B', x: 418, y: 156 },
    { id: 'assets',        label: 'Devices',           count: data?.assets        ?? 0, color: '#2DD4BF', x: 618, y: 100 },
    { id: 'ports',         label: 'Ports',             count: data?.ports         ?? 0, color: '#94A3B8', x: 618, y: 200 },
    { id: 'connections',   label: 'Links',             count: data?.connections   ?? 0, color: '#34D399', x: 800, y: 140 },
    { id: 'clashes',       label: 'Open Clashes',      count: data?.openClashes   ?? 0, color: '#F87171', x: 800, y: 220 },
  ] as const;

  const edges = [
    { from: 'projects',    to: 'buildings',     animated: true  },
    { from: 'buildings',   to: 'telecomSpaces', animated: true  },
    { from: 'buildings',   to: 'racks',         animated: true  },
    { from: 'racks',       to: 'assets',        animated: true  },
    { from: 'racks',       to: 'ports',         animated: false },
    { from: 'assets',      to: 'connections',   animated: true  },
    { from: 'ports',       to: 'connections',   animated: false },
    { from: 'connections', to: 'clashes',       animated: false },
  ];

  const findNode = (id: string) => nodes.find((n) => n.id === id);

  return (
    <>
      <defs>
        <style>{`
          .topo-edge   { stroke-dasharray: 8 5; animation: topo-dash 1.8s linear infinite; }
          .topo-static { opacity: 0.3; }
          @keyframes topo-dash { to { stroke-dashoffset: -26; } }
        `}</style>
        <filter id="t-glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="6" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>

      {/* Grid background */}
      <defs>
        <pattern id="topo-grid" width="40" height="40" patternUnits="userSpaceOnUse">
          <path d="M 40 0 L 0 0 0 40" fill="none"
            stroke="rgba(40,68,116,0.12)" strokeWidth="0.5" />
        </pattern>
      </defs>
      <rect x="0" y="0" width={TOPO_W} height={TOPO_H}
        fill="url(#topo-grid)" />

      {/* Edges */}
      {edges.map((e) => {
        const s = findNode(e.from); const t = findNode(e.to);
        if (!s || !t) return null;
        const x1 = s.x + NW; const y1 = s.y + NH / 2;
        const x2 = t.x;       const y2 = t.y + NH / 2;
        const mx = (x1 + x2) / 2;
        const hot = hovered === e.from || hovered === e.to;
        return (
          <path
            key={`${e.from}-${e.to}`}
            d={`M ${x1},${y1} C ${mx},${y1} ${mx},${y2} ${x2},${y2}`}
            fill="none"
            stroke={hot ? '#2DD4BF' : 'rgba(45,212,191,0.28)'}
            strokeWidth={hot ? 2 : 1.2}
            className={e.animated ? 'topo-edge' : 'topo-static'}
          />
        );
      })}

      {/* Nodes */}
      {nodes.map((n) => {
        const isHot = hovered === n.id;
        const route = TOPOLOGY_ROUTES[n.id as keyof typeof TOPOLOGY_ROUTES];
        const clickable = !!route && (!!onNavigate || !!onClose);
        return (
          <g
            key={n.id}
            data-testid={`topo-node-${n.id}`}
            transform={`translate(${n.x},${n.y})`}
            style={{ cursor: clickable ? 'pointer' : 'default' }}
            onMouseEnter={() => setHovered(n.id)}
            onMouseLeave={() => setHovered(null)}
            onClick={() => {
              if (route && onNavigate) {
                onNavigate(route);
                onClose?.();
              }
            }}
          >
            {/* Glow halo */}
            {isHot && (
              <rect x="-5" y="-5" width={NW + 10} height={NH + 10} rx="12"
                fill="none" stroke={n.color} strokeWidth="1.5" opacity="0.5"
                style={{ filter: `drop-shadow(0 0 10px ${n.color})` }}
              />
            )}
            {/* Card body */}
            <rect x="0" y="0" width={NW} height={NH} rx="9"
              fill={isHot ? 'rgba(22,24,34,0.99)' : 'rgba(14,20,38,0.92)'}
              stroke={isHot ? n.color : 'rgba(40,68,116,0.8)'}
              strokeWidth={isHot ? 1.8 : 1}
            />
            {/* Left accent stripe */}
            <rect x="0" y="0" width="5" height={NH} rx="3"
              fill={n.color} opacity="0.92" />
            {/* Label — high-contrast, large */}
            <text x="17" y="28"
              fontFamily="'Inter',system-ui,sans-serif"
              fontSize="13" fontWeight="700"
              fill={isHot ? '#F4F4F5' : 'rgba(226,232,240,0.82)'}
              letterSpacing="0.06em"
              style={{ textTransform: 'uppercase' }}
            >{n.label}</text>
            {/* Count — dominant, coloured */}
            <text x="17" y="62"
              fontFamily="'Inter',system-ui,sans-serif"
              fontSize="30" fontWeight="800"
              fill={isHot ? n.color : 'rgba(241,245,249,0.92)'}
            >
              {isLoading ? '—' : n.count.toLocaleString()}
            </text>
            {/* Nav arrow */}
            {clickable && (
              <text x={NW - 18} y="46"
                fontFamily="monospace" fontSize="18"
                fill={isHot ? n.color : 'rgba(40,68,116,0.5)'}
              >›</text>
            )}
          </g>
        );
      })}

      {/* Footer legend */}
      <text x={TOPO_W - 8} y={TOPO_H - 8}
        fontFamily="'Inter',system-ui,sans-serif" fontSize="11"
        fill="rgba(40,68,116,0.7)" textAnchor="end" letterSpacing="0.1em">
        LIVE · REFRESHES EVERY 30 s · CLICK NODE TO NAVIGATE
      </text>
    </>
  );
}

// =============================================================================
//  Architecture content  (pure SVG <g> — no <svg> wrapper)
// =============================================================================

const ENG = [
  { id: 'sie',      label: 'SIE',       sub: 'Smart Ingestion',    color: '#2DD4BF', x: 6   },
  { id: 'calendar', label: 'Calendar',  sub: 'Scheduling Engine',  color: '#38BDF8', x: 164 },
  { id: 'm365',     label: 'M365 Sync', sub: 'Microsoft Graph',    color: '#818CF8', x: 322 },
  { id: 'fris',     label: 'FRIS',      sub: 'Feature Readiness',  color: '#A78BFA', x: 480 },
  { id: 'pfe',      label: 'PFE',       sub: 'File Explorer',      color: '#F59E0B', x: 638 },
  { id: 'license',  label: 'License',   sub: 'RCDD Gate',          color: '#34D399', x: 796 },
];
const REN = [
  { id: 'dash',    label: 'Dashboard', sub: 'Command Station', color: '#2DD4BF', x: 10  },
  { id: 'emmy',   label: 'Emmy AI',   sub: 'Eng. Advisor',    color: '#F59E0B', x: 204 },
  { id: 'sie-ui', label: 'SIE UI',    sub: 'Ingest Dialog',   color: '#2DD4BF', x: 398 },
  { id: 'frpan',  label: 'FRIS Panel',sub: 'Observability',   color: '#A78BFA', x: 592 },
  { id: 'modules',label: 'Modules',   sub: '15+ pages',       color: '#94A3B8', x: 786 },
];

function EngineCard({ x, y, label, sub, color, testId }: {
  x: number; y: number; label: string; sub: string; color: string; testId: string;
}): JSX.Element {
  return (
    <g data-testid={testId} transform={`translate(${x},${y})`}>
      <rect x="0" y="0" width={EW} height={EH} rx="8"
        fill="rgba(14,20,38,0.94)" stroke="rgba(40,68,116,0.75)" strokeWidth="1" />
      <rect x="0" y="0" width="4" height={EH} rx="2" fill={color} opacity="0.9" />
      <text x="14" y="24"
        fontFamily="'Inter',system-ui,sans-serif" fontSize="14"
        fontWeight="800" fill="#F1F5F9" letterSpacing="0.04em">{label}</text>
      <text x="14" y="44"
        fontFamily="'Inter',system-ui,sans-serif" fontSize="11"
        fill="rgba(148,163,184,0.85)">{sub}</text>
    </g>
  );
}

function ArchitectureContent(): JSX.Element {
  return (
    <>
      <defs>
        <style>{`.ipc-pulse { animation: ipc-g 2.2s ease-in-out infinite alternate; } @keyframes ipc-g { from{opacity:.5}to{opacity:1} }`}</style>
        <linearGradient id="a-ipc" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%"   stopColor="#1B9480" stopOpacity="0.45" />
          <stop offset="50%"  stopColor="#25B5A8" stopOpacity="0.95" />
          <stop offset="100%" stopColor="#1B9480" stopOpacity="0.45" />
        </linearGradient>
        <linearGradient id="a-db" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%"   stopColor="#1e3a5f" stopOpacity="0.8" />
          <stop offset="50%"  stopColor="#1e4a6f" stopOpacity="1"   />
          <stop offset="100%" stopColor="#1e3a5f" stopOpacity="0.8" />
        </linearGradient>
        <pattern id="arch-grid" width="40" height="40" patternUnits="userSpaceOnUse">
          <path d="M 40 0 L 0 0 0 40" fill="none"
            stroke="rgba(40,68,116,0.10)" strokeWidth="0.5" />
        </pattern>
      </defs>

      {/* Grid */}
      <rect x="0" y="0" width={ARCH_W} height={ARCH_H} fill="url(#arch-grid)" />

      {/* External source labels */}
      <text x={322 + EW / 2} y="10" textAnchor="middle"
        fontFamily="'Inter',system-ui,sans-serif" fontSize="11"
        fill="rgba(129,140,248,0.65)" letterSpacing="0.1em">
        ↑ MICROSOFT GRAPH API
      </text>
      <text x={638 + EW / 2} y="10" textAnchor="middle"
        fontFamily="'Inter',system-ui,sans-serif" fontSize="11"
        fill="rgba(245,158,11,0.65)" letterSpacing="0.1em">
        ↑ LOCAL FILE SYSTEM
      </text>

      {/* ── Zone: Main Process ── */}
      <rect x="4" y="18" width={ARCH_W - 8} height="220" rx="12"
        fill="rgba(12,18,32,0.85)" stroke="rgba(37,181,168,0.22)" strokeWidth="1" />
      <text x="18" y="40"
        fontFamily="'Inter',system-ui,sans-serif" fontSize="11"
        fontWeight="700" fill="rgba(37,181,168,0.7)" letterSpacing="0.2em">
        MAIN PROCESS · NODE.JS + ELECTRON
      </text>

      {/* Engine nodes */}
      {ENG.map((n) => (
        <EngineCard key={n.id} x={n.x} y={52} label={n.label} sub={n.sub}
          color={n.color} testId={`arch-node-${n.id}`} />
      ))}

      {/* SQLite bar */}
      <rect x="4" y="134" width={ARCH_W - 8} height="36" fill="url(#a-db)" />
      <text x={ARCH_W / 2} y="158" textAnchor="middle"
        fontFamily="'IBM Plex Mono','Courier New',monospace" fontSize="13"
        fontWeight="700" fill="rgba(147,197,253,0.88)" letterSpacing="0.06em">
        SQLite · Drizzle ORM · Offline-First Persistence Layer
      </text>

      {/* Engine → SQLite connectors */}
      {ENG.map((n) => (
        <line key={`c-${n.id}`}
          x1={n.x + EW / 2} y1={52 + EH}
          x2={n.x + EW / 2} y2={134}
          stroke={n.color} strokeWidth="1.5" opacity="0.3" strokeDasharray="4 4" />
      ))}

      {/* FRIS probe signals */}
      {ENG.filter((n) => n.id !== 'fris').map((n) => (
        <path key={`fp-${n.id}`}
          d={`M ${n.x + EW / 2},${52 + EH + 6} Q ${n.x + EW / 2},175 ${480 + EW / 2},175`}
          fill="none" stroke="rgba(167,139,250,0.18)" strokeWidth="1" strokeDasharray="5 4" />
      ))}
      <text x={480 + EW + 10} y="176"
        fontFamily="'Inter',system-ui,sans-serif" fontSize="10"
        fill="rgba(167,139,250,0.45)" letterSpacing="0.08em">← probe signals</text>

      {/* Boot order */}
      <text x="18" y="228"
        fontFamily="'Inter',system-ui,sans-serif" fontSize="10"
        fill="rgba(40,68,116,0.9)" letterSpacing="0.08em">
        Boot order: Infrastructure → Database → Engines → IPC Handlers → Renderer
      </text>

      {/* ── IPC Bridge ── */}
      <rect x="4" y="248" width={ARCH_W - 8} height="32" rx="6"
        fill="url(#a-ipc)" className="ipc-pulse" />
      <text x={ARCH_W / 2} y="270" textAnchor="middle"
        fontFamily="'Inter',system-ui,sans-serif" fontSize="13"
        fontWeight="700" fill="rgba(224,255,254,0.92)" letterSpacing="0.14em">
        IPC BRIDGE · contextBridge · Preload (cjs)
      </text>

      {/* ── Zone: Renderer ── */}
      <rect x="4" y="290" width={ARCH_W - 8} height="118" rx="12"
        fill="rgba(12,18,32,0.80)" stroke="rgba(37,181,168,0.16)" strokeWidth="1" />
      <text x="18" y="312"
        fontFamily="'Inter',system-ui,sans-serif" fontSize="11"
        fontWeight="700" fill="rgba(37,181,168,0.6)" letterSpacing="0.2em">
        RENDERER · REACT 18 + VITE 5
      </text>

      {/* Renderer nodes */}
      {REN.map((n) => (
        <EngineCard key={n.id} x={n.x} y={320} label={n.label} sub={n.sub}
          color={n.color} testId={`arch-rnode-${n.id}`} />
      ))}

      {/* IPC → Renderer connectors */}
      {REN.map((n) => (
        <line key={`ir-${n.id}`}
          x1={n.x + EW / 2} y1={280}
          x2={n.x + EW / 2} y2={320}
          stroke={n.color} strokeWidth="1.5" opacity="0.45" />
      ))}

      {/* Footer */}
      <text x={ARCH_W - 8} y={ARCH_H - 8}
        fontFamily="'Inter',system-ui,sans-serif" fontSize="11"
        fill="rgba(40,68,116,0.7)" textAnchor="end" letterSpacing="0.1em">
        BIM OPS PLATFORM · OFFLINE-FIRST · ELECTRON + REACT + SQLITE
      </text>
    </>
  );
}
